import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

const CLAVE_RECARGA_RECIENTE = "tentacion_recarga_por_error_chunk";

function pareceErrorDeChunkDesactualizado(error: Error): boolean {
  const mensaje = `${error.message} ${error.name}`.toLowerCase();
  return (
    mensaje.includes("dynamically imported module") ||
    mensaje.includes("failed to fetch") ||
    mensaje.includes("loading chunk") ||
    mensaje.includes("importing a module script failed")
  );
}

/**
 * Después de publicar una actualización, un navegador con la app ya abierta
 * (o con el shell cacheado por el service worker) puede intentar pedir un
 * archivo .js con un nombre/hash que ya no existe en el servidor nuevo —
 * React no tiene forma de recuperarse solo de eso y la pantalla queda en
 * blanco hasta que el usuario recarga a mano. Este boundary detecta ese caso
 * específico y recarga automáticamente UNA vez; para cualquier otro error,
 * muestra un aviso con botón en vez de una pantalla en blanco silenciosa.
 */
export default class ErrorBoundaryApp extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("Error atrapado por ErrorBoundaryApp:", error, info.componentStack);

    if (pareceErrorDeChunkDesactualizado(error)) {
      const yaRecargoHaceUnRato = sessionStorage.getItem(CLAVE_RECARGA_RECIENTE);
      if (!yaRecargoHaceUnRato) {
        sessionStorage.setItem(CLAVE_RECARGA_RECIENTE, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-crema p-6 text-center">
          <span className="text-4xl">🍓</span>
          <p className="font-display text-lg font-bold text-texto">Se actualizó la app</p>
          <p className="max-w-sm text-sm text-texto-secundario">
            Hay una versión más reciente disponible. Toca el botón para cargarla.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="boton-primario px-6 py-2.5 text-sm"
          >
            Recargar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
